import {
  type CellData,
  type ColumnDef,
  createColumnHelper,
  type RowData,
  type TableFeatures,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { type KeyboardEvent, type ReactNode, useEffect, useEffectEvent, useId, useRef, useState } from "react";
import { cn } from "../cn";
import { Checkbox } from "../components/checkbox";
import { useMediaQuery } from "../hooks/use-media-query";
import { type CellInputMode, nextEditable, startsEdit, stepColumn } from "./cell-cursor";
import { type PresencePerson, PresenceStack } from "./presence-stack";
import { selectRange, toggleAll, toggleSelected } from "./selection";

// Virtualized grid of the work lists (design-system.md §4.1): TanStack Table for the column model, TanStack Virtual
// for the rows, an ARIA grid with an active row for the keyboard (arrows or j/k move, Enter opens). Paging and
// sorting stay on the server (Q-API-03): the grid renders what it gets and asks for more at the end. When the
// columns do not fit, the grid scrolls inside its own box and the first column (code or name) stays (ADR-050).
// With `selection` the rows can be selected for a bulk action: a checkbox in the first cell, `x` on the active row,
// Shift + arrows or Shift + click for a range.
// With `editing` the columns marked `editable` are changed in place (§4.9): the cursor is then a cell (left and right
// arrows too); Enter, F2, a digit or a click open it, Tab goes on to the next one, Esc gives up. The grid owns the
// cursor and the text being typed; the screen owns the values, and gets each one when its cell is left. A cell with
// `editing.options` is chosen from a list instead of typed.
// With `rowPresence` the rows others are on show who (ADR-279), and the active row is where this user is.
// With `rowChanges` the rows someone else just changed are tinted, with who in words in the row, and a screen reader
// hears it once, politely (ADR-308). No motion: the tint comes and goes.

declare module "@tanstack/react-table" {
  interface ColumnMeta<in out TFeatures extends TableFeatures, in out TData extends RowData, TValue extends CellData> {
    /** CSS grid track: "8rem", "minmax(12rem, 2fr)". */
    width?: string;
    /** Numbers and amounts are right aligned (design-system.md §2.4). */
    align?: "start" | "end";
    /** The cells of the column are changed in place, when the grid has `editing`. */
    editable?: boolean;
    /** What an editable cell takes: a number starts being edited by typing it. Text by default. */
    inputMode?: CellInputMode;
  }
}

const features = tableFeatures({});
type GridFeatures = typeof features;

/** Column helper of a grid of `T`: `const col = gridColumns<Row>(); col.accessor("name", { header, meta })`. */
export const gridColumns = <T extends RowData>() => createColumnHelper<GridFeatures, T>();

// biome-ignore lint/suspicious/noExplicitAny: the value type differs per column, as in TanStack's own examples
export type GridColumn<T extends RowData> = ColumnDef<GridFeatures, T, any>;

interface DataGridProps<T extends RowData> {
  /** Accessible name of the grid ("Linesheets"). */
  label: string;
  rows: T[];
  columns: GridColumn<T>[];
  getRowId: (row: T) => string;
  /** Row opened outside the grid (the detail panel): it becomes the cursor. Without it the grid keeps its own. */
  activeRowId?: string | null | undefined;
  onActiveRowChange?: (row: T) => void;
  /** Enter or click on a row. */
  onOpen?: (row: T) => void;
  /** Pixels. By default the `--row-h` of the density (design-system.md §2.5), read from the grid. */
  rowHeight?: number;
  /** Called when the last rows come into view: load the next page. */
  onEndReached?: () => void;
  /** Rendered under the rows (loading more, end of the list). */
  footer?: ReactNode;
  empty?: ReactNode;
  className?: string;
  /** Rows selected for a bulk action: the screen keeps the set, the grid changes it. */
  selection?: GridSelection<T>;
  /** In-cell editing of the columns with `meta.editable`: without it the grid has no cell cursor. */
  editing?: GridEditing<T>;
  /** Who else is on each row, and where this user is (ADR-279). */
  rowPresence?: GridPresence | undefined;
  /** The rows someone else just changed, and who (ADR-308). */
  rowChanges?: GridChanges | undefined;
}

export interface GridChanges {
  /** The rows changed by someone else, by row id: who, shown in the row, and the full words for hover and readers. */
  byRow: ReadonlyMap<string, { name: string; label: string }>;
  /** What a screen reader hears of the change, politely; empty when no row is highlighted. */
  announcement: string;
  /** Grows with each change: the same sentence for a new change is heard again. */
  announcementKey: number;
}

export interface GridPresence {
  /** The people on each row, by row id, as the room lets this user see them. */
  byRow: ReadonlyMap<string, readonly PresencePerson[]>;
  /** The active row changed: the row this user is on, or null. */
  onRowChange: (rowId: string | null) => void;
  /** What the people of a row are, in the words of the screen ("Also on this row"). */
  label: string;
}

export interface GridEditing<T> {
  /** What the editor starts from: the value of the cell as the user would type it. */
  value: (row: T, columnId: string) => string;
  /** A cell of an editable column that cannot be changed now (a price not calculated yet). */
  canEdit?: (row: T, columnId: string) => boolean;
  /**
   * The cell was left with this text (Enter, Tab, blur): the screen validates and keeps it. With `submit` it was left
   * with ⌘/Ctrl + Enter: the screen also saves what it keeps.
   */
  onCommit: (row: T, columnId: string, text: string, how: { submit: boolean }) => void;
  /** Accessible name of the editor ("Wholesale, ST-1042"). */
  cellLabel: (row: T, columnId: string) => string;
  /**
   * The values a cell is chosen from: its editor is a select, and `value` and `onCommit` speak in option values. The
   * screen lists the empty choice too, when the cell can be emptied.
   */
  options?: (row: T, columnId: string) => readonly CellOption[] | undefined;
}

export interface CellOption {
  value: string;
  label: string;
}

interface CellEdit {
  rowId: string;
  columnId: string;
  text: string;
}

interface GridSelection<T> {
  selected: ReadonlySet<string>;
  onChange: (selected: Set<string>) => void;
  /** Accessible name of the checkbox of a row ("Select ST-1042"). */
  rowLabel: (row: T) => string;
  allLabel: string;
}

export function DataGrid<T extends RowData>(props: DataGridProps<T>) {
  // TanStack Virtual keeps its state outside React: the compiler must not memoize this component
  "use no memo";
  const { label, rows, columns, getRowId, onOpen, onEndReached, footer, empty, className, selection, editing } = props;
  const table = useTable({ features, columns, data: rows, getRowId: (row) => getRowId(row) });
  const scrollRef = useRef<HTMLDivElement>(null);
  const densityRowHeight = useDensityRowHeight();
  const rowHeight = props.rowHeight ?? densityRowHeight;
  const idPrefix = useId();
  const [localActive, setLocalActive] = useState<string | null>(null);
  const activeId = props.activeRowId ?? localActive;
  const tableRows = table.getRowModel().rows;
  const activeIndex = tableRows.findIndex((r) => r.id === activeId);
  const rowIds = tableRows.map((r) => r.id);
  // where a range starts: the last row selected one by one
  const anchor = useRef(-1);
  const shiftClick = useRef(false);

  const tableRef = useRef<HTMLTableElement>(null);
  const leafColumns = table.getAllLeafColumns();
  const editableCols = editing ? leafColumns.flatMap((c, i) => (c.columnDef.meta?.editable ? [i] : [])) : [];
  const [activeColId, setActiveColId] = useState<string | null>(null);
  // until the cursor is moved it sits on the first column that can be edited
  const movedTo = leafColumns.findIndex((c) => c.id === activeColId);
  const activeCol = editing ? (movedTo >= 0 ? movedTo : (editableCols[0] ?? 0)) : -1;
  const [cellEdit, setCellEdit] = useState<CellEdit | null>(null);
  const editIndex = cellEdit ? tableRows.findIndex((r) => r.id === cellEdit.rowId) : -1;
  // an edit whose row is gone (a refetch, a filter) is over
  const edit = editIndex >= 0 ? cellEdit : null;
  // the cell to bring into view once it is rendered: only after a move of the cursor, never on a plain render
  const reveal = useRef(false);

  const cellId = (rowId: string, columnId: string) => `${idPrefix}-${rowId}-${columnId}`;

  function canEditCell(rowIndex: number, colIndex: number): boolean {
    const row = tableRows[rowIndex];
    const column = leafColumns[colIndex];
    if (!editing || !row || !column?.columnDef.meta?.editable) return false;
    return editing.canEdit?.(row.original, column.id) ?? true;
  }

  function startEdit(rowIndex: number, colIndex: number, text?: string) {
    const row = tableRows[rowIndex];
    const column = leafColumns[colIndex];
    if (!editing || !row || !column) return;
    moveTo(rowIndex);
    setActiveColId(column.id);
    setCellEdit({ rowId: row.id, columnId: column.id, text: text ?? editing.value(row.original, column.id) });
    reveal.current = true;
  }

  /** Leaves the cell being edited: with `text` the screen gets it; then on to the next cell, or back to the grid. */
  function endEdit(text: string | null, then: 1 | -1 | "grid" | "away", submit = false) {
    if (!editing || !edit) return;
    const row = tableRows[editIndex];
    if (row && text !== null) editing.onCommit(row.original, edit.columnId, text, { submit });
    const from = { row: editIndex, col: leafColumns.findIndex((c) => c.id === edit.columnId) };
    const next =
      then === 1 || then === -1
        ? nextEditable(from, then, { rows: tableRows.length, editableCols, canEdit: canEditCell })
        : null;
    if (next) return startEdit(next.row, next.col);
    setCellEdit(null);
    if (then !== "away") tableRef.current?.focus();
  }

  function select(index: number, range: boolean) {
    const id = rowIds[index];
    if (!selection || id === undefined) return;
    if (range && anchor.current >= 0) {
      selection.onChange(selectRange(selection.selected, rowIds, anchor.current, index));
    } else {
      anchor.current = index;
      selection.onChange(toggleSelected(selection.selected, id));
    }
  }

  // eslint-disable-next-line react-hooks/incompatible-library -- known and handled: "use no memo" above
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    getItemKey: (index) => tableRows[index]?.id ?? index,
    overscan: 10,
    // the row being edited stays mounted when it scrolls out of view: its editor keeps the text and the focus
    rangeExtractor: (range) => {
      const indexes = defaultRangeExtractor(range);
      return editIndex < 0 || indexes.includes(editIndex) ? indexes : [...indexes, editIndex].sort((a, b) => a - b);
    },
  });
  const items = virtualizer.getVirtualItems();
  const lastIndex = items.at(-1)?.index ?? -1;

  // biome-ignore lint/correctness/useExhaustiveDependencies: the cached row sizes are stale when the density changes
  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer, rowHeight]);

  useEffect(() => {
    if (onEndReached && tableRows.length > 0 && lastIndex >= tableRows.length - 5) onEndReached();
  }, [lastIndex, tableRows.length, onEndReached]);

  const activeRowId = tableRows[activeIndex]?.id;
  const activeColumnId = leafColumns[activeCol]?.id;
  const presence = props.rowPresence;
  const showsPresence = presence !== undefined;
  const tellRow = useEffectEvent((rowId: string | null) => presence?.onRowChange(rowId));
  // biome-ignore lint/correctness/useExhaustiveDependencies: told again when the grid starts showing who is here
  useEffect(() => {
    tellRow(activeRowId ?? null);
  }, [activeRowId, showsPresence]);
  useEffect(() => {
    if (!reveal.current || activeRowId === undefined || activeColumnId === undefined) return;
    reveal.current = false;
    const cell = document.getElementById(`${idPrefix}-${activeRowId}-${activeColumnId}`);
    const scroller = scrollRef.current;
    if (!cell || !scroller) return;
    cell.scrollIntoView({ block: "nearest", inline: "nearest" });
    // the first column is sticky: a cell scrolled to the left edge would sit under it
    const sticky = cell.parentElement?.firstElementChild;
    if (!sticky || sticky === cell) return;
    const covered = sticky.getBoundingClientRect().right - cell.getBoundingClientRect().left;
    if (covered > 0) scroller.scrollLeft -= covered;
  }, [idPrefix, activeRowId, activeColumnId]);

  const template = leafColumns.map((c) => c.columnDef.meta?.width ?? "minmax(8rem, 1fr)").join(" ");

  function moveTo(index: number) {
    const row = tableRows[Math.max(0, Math.min(index, tableRows.length - 1))];
    if (!row) return;
    setLocalActive(row.id);
    props.onActiveRowChange?.(row.original);
    virtualizer.scrollToIndex(row.index, { align: "auto" });
  }

  function moveCol(delta: number) {
    setActiveColId(leafColumns[stepColumn(activeCol, delta, leafColumns.length)]?.id ?? null);
    reveal.current = true;
  }

  function onKeyDown(e: KeyboardEvent<HTMLTableElement>) {
    // the editor of a cell handles its own keys
    if (e.target instanceof HTMLSelectElement) return;
    if (e.target instanceof HTMLInputElement && e.target.type !== "checkbox") return;
    const editable = canEditCell(activeIndex, activeCol);
    const cellKeys: Record<string, () => void> = editing
      ? {
          ArrowLeft: () => moveCol(-1),
          ArrowRight: () => moveCol(1),
          ...(editable && {
            Enter: () => startEdit(activeIndex, activeCol),
            F2: () => startEdit(activeIndex, activeCol),
          }),
        }
      : {};
    const keys: Record<string, () => void> = {
      ArrowDown: () => moveTo(activeIndex + 1),
      j: () => moveTo(activeIndex + 1),
      ArrowUp: () => moveTo(activeIndex - 1),
      k: () => moveTo(activeIndex - 1),
      Home: () => moveTo(0),
      End: () => moveTo(tableRows.length - 1),
      Enter: () => {
        const row = tableRows[activeIndex];
        if (row) onOpen?.(row.original);
      },
      ...cellKeys,
    };
    const extend = (index: number) => {
      if (anchor.current < 0) anchor.current = Math.max(0, activeIndex);
      moveTo(index);
      if (selection) selection.onChange(selectRange(selection.selected, rowIds, anchor.current, index));
    };
    const selectionKeys: Record<string, () => void> = {
      x: () => select(activeIndex, false),
      "Shift+ArrowDown": () => extend(Math.min(activeIndex + 1, tableRows.length - 1)),
      "Shift+ArrowUp": () => extend(Math.max(activeIndex - 1, 0)),
    };
    const modified = e.metaKey || e.ctrlKey || e.altKey;
    const typed =
      editable && startsEdit(e.key, leafColumns[activeCol]?.columnDef.meta?.inputMode ?? "text")
        ? () => startEdit(activeIndex, activeCol, e.key)
        : undefined;
    const action = modified
      ? undefined
      : ((selection && selectionKeys[e.shiftKey ? `Shift+${e.key}` : e.key]) ??
        (e.shiftKey ? undefined : (keys[e.key] ?? typed)));
    // a checkbox handles its own Space; the other keys still move the grid
    if (!action || (e.target instanceof HTMLInputElement && e.key === " ")) return;
    e.preventDefault();
    action();
  }

  if (tableRows.length === 0 && empty) return <div className={className}>{empty}</div>;

  // semantic table elements laid out with CSS grid, so rows can be absolutely positioned by the virtualizer; at least
  // 15rem tall, so in a short window (400% zoom) the page scrolls instead of a grid reduced to its header (ADR-050)
  return (
    <div ref={scrollRef} className={cn("relative min-h-60 overflow-auto rounded-lg border border-border", className)}>
      <table
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <table role="grid"> is the WAI-ARIA APG data grid
        role="grid"
        aria-label={label}
        aria-rowcount={tableRows.length + 1}
        aria-multiselectable={selection ? true : undefined}
        aria-activedescendant={
          activeRowId === undefined
            ? undefined
            : editing && activeColumnId
              ? cellId(activeRowId, activeColumnId)
              : `${idPrefix}-${activeRowId}`
        }
        ref={tableRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="grid w-max min-w-full focus-visible:outline-offset-[-2px]"
      >
        <thead className="sticky top-0 z-10 grid border-b border-border bg-muted text-xs font-medium text-muted-foreground">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} aria-rowindex={1} className="grid" style={{ gridTemplateColumns: template }}>
              {group.headers.map((header, index) => (
                <th
                  key={header.id}
                  className={cn(
                    "flex items-center px-3 py-2 text-left font-medium",
                    index === 0 && [stickyCell, "bg-muted"],
                    index === 0 && selection && "gap-2",
                    header.column.columnDef.meta?.align === "end" && "justify-end text-right",
                  )}
                >
                  {index === 0 && selection && (
                    <Checkbox
                      aria-label={selection.allLabel}
                      checked={rowIds.length > 0 && rowIds.every((id) => selection.selected.has(id))}
                      indeterminate={
                        rowIds.some((id) => selection.selected.has(id)) &&
                        !rowIds.every((id) => selection.selected.has(id))
                      }
                      onChange={() => selection.onChange(toggleAll(selection.selected, rowIds))}
                    />
                  )}
                  {header.isPlaceholder ? null : <table.FlexRender header={header} />}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="relative grid" style={{ height: virtualizer.getTotalSize() }}>
          {items.map((item) => {
            const row = tableRows[item.index];
            if (!row) return null;
            const active = row.id === activeId;
            const changedBy = props.rowChanges?.byRow.get(row.id);
            const others = changedBy ? undefined : presence?.byRow.get(row.id);
            return (
              <tr
                key={row.id}
                id={`${idPrefix}-${row.id}`}
                aria-rowindex={item.index + 2}
                aria-selected={selection ? selection.selected.has(row.id) : active}
                onClick={(e) => {
                  // the checkbox and its label select the row, they do not open it
                  if (e.target instanceof Element && e.target.closest("label")) return;
                  const target = e.target;
                  if (editing && target instanceof Element) {
                    // a click inside the editor places the caret; on an editable cell it opens it instead of the row
                    if (target.closest("input, select")) return;
                    const col = leafColumns.findIndex((c) => c.id === target.closest("td")?.dataset.col);
                    if (canEditCell(row.index, col)) return startEdit(row.index, col);
                    if (col >= 0) setActiveColId(leafColumns[col]?.id ?? null);
                  }
                  moveTo(row.index);
                  onOpen?.(row.original);
                }}
                className={cn(
                  "absolute inset-x-0 grid cursor-default items-center border-b border-border bg-background text-sm hover:bg-accent",
                  active && "bg-muted",
                  // opaque: the sticky first cell inherits it, and the cells scrolled under it must not show through
                  changedBy && "bg-[color-mix(in_oklch,var(--brand)_10%,var(--background))]",
                )}
                style={{ gridTemplateColumns: template, height: rowHeight, transform: `translateY(${item.start}px)` }}
              >
                {row.getAllCells().map((cell, index) => {
                  const columnId = cell.column.id;
                  const editingCell = edit?.rowId === row.id && edit.columnId === columnId;
                  const cursor = editing && active && index === activeCol;
                  const options = editingCell ? editing?.options?.(row.original, columnId) : undefined;
                  return (
                    <td
                      key={cell.id}
                      {...(editing && {
                        id: cellId(row.id, columnId),
                        "data-col": columnId,
                        "aria-readonly": !canEditCell(row.index, index),
                      })}
                      className={cn(
                        "truncate px-3 tabular-nums",
                        index === 0 && [stickyCell, "content-center self-stretch bg-inherit"],
                        index === 0 && selection && "flex items-center gap-2",
                        index === 0 && active && "shadow-[inset_2px_0_0_var(--brand)]",
                        index === 0 && (others || changedBy) && "pr-14",
                        cell.column.columnDef.meta?.align === "end" && "text-right",
                        editing && index > 0 && "content-center self-stretch",
                        cursor && index > 0 && "shadow-[inset_0_0_0_2px_var(--brand)]",
                        editingCell && "overflow-visible p-0",
                      )}
                    >
                      {index === 0 && selection && (
                        <Checkbox
                          aria-label={selection.rowLabel(row.original)}
                          checked={selection.selected.has(row.id)}
                          // the keyboard selects with `x` on the active row: the focus stays on the grid
                          tabIndex={-1}
                          // the click says whether Shift was held, the change that follows selects
                          onClick={(e) => {
                            shiftClick.current = e.shiftKey;
                          }}
                          onChange={() => select(row.index, shiftClick.current)}
                        />
                      )}
                      {editing && edit && editingCell ? (
                        options ? (
                          <CellChoice
                            label={editing.cellLabel(row.original, columnId)}
                            value={edit.text}
                            options={options}
                            onEnd={endEdit}
                          />
                        ) : (
                          <CellEditor
                            label={editing.cellLabel(row.original, columnId)}
                            text={edit.text}
                            inputMode={cell.column.columnDef.meta?.inputMode ?? "text"}
                            align={cell.column.columnDef.meta?.align}
                            onChange={(text) => setCellEdit({ ...edit, text })}
                            onEnd={endEdit}
                          />
                        )
                      ) : (
                        <table.FlexRender cell={cell} />
                      )}
                      {index === 0 && changedBy && (
                        <span
                          title={changedBy.label}
                          className="absolute top-1/2 right-1 flex max-w-12 -translate-y-1/2 items-center gap-0.5 rounded-sm border border-brand bg-background px-1 text-[0.625rem] font-medium text-foreground"
                        >
                          <span aria-hidden className="truncate">
                            {changedBy.name}
                          </span>
                          <span className="sr-only">{changedBy.label}</span>
                        </span>
                      )}
                      {index === 0 && presence && others && (
                        <PresenceStack
                          people={others}
                          label={presence.label}
                          size="xs"
                          max={2}
                          className="absolute top-1/2 right-1 -translate-y-1/2"
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footer}
      {props.rowChanges && <ChangesAnnouncer changes={props.rowChanges} />}
    </div>
  );
}

/** How long the live region stays empty before a new sentence, so a reader hears the same sentence again. */
const ANNOUNCE_AFTER_MS = 100;

/** The polite live region of the changes of others: emptied, then the sentence of each change. */
function ChangesAnnouncer({ changes }: { changes: GridChanges }) {
  const { announcement, announcementKey } = changes;
  const region = useRef<HTMLParagraphElement>(null);
  // the region's text is set here, not rendered: emptied, then the sentence, for every change
  // biome-ignore lint/correctness/useExhaustiveDependencies: said again for every change, even with the same words
  useEffect(() => {
    const node = region.current;
    if (!node) return;
    node.textContent = "";
    if (!announcement) return;
    const say = setTimeout(() => {
      node.textContent = announcement;
    }, ANNOUNCE_AFTER_MS);
    return () => clearTimeout(say);
  }, [announcement, announcementKey]);
  return <p ref={region} aria-live="polite" className="sr-only" />;
}

/** With the text to keep, or null to give up; then where the cursor goes, and whether the screen saves. */
type EndEdit = (text: string | null, then: 1 | -1 | "grid" | "away", submit?: boolean) => void;

/** The cell is left once: the blur that follows a key or a choice must not keep the value a second time. */
function useEndOnce(onEnd: EndEdit): EndEdit {
  const left = useRef(false);
  return (text, then, submit = false) => {
    if (left.current) return;
    left.current = true;
    onEnd(text, then, submit);
  };
}

interface CellEditorProps {
  label: string;
  text: string;
  inputMode: CellInputMode;
  align: "start" | "end" | undefined;
  onChange: (text: string) => void;
  onEnd: EndEdit;
}

/**
 * The input of the cell being edited, as large as the cell. Enter keeps the text, Tab keeps it and opens the next
 * cell, Esc gives up; leaving the cell any other way keeps it. ⌘/Ctrl + Enter keeps it and tells the screen to save.
 */
function CellEditor({ label, text, inputMode, align, onChange, onEnd }: CellEditorProps) {
  const end = useEndOnce(onEnd);
  return (
    <input
      // biome-ignore lint/a11y/noAutofocus: the user opened this cell to type in it
      autoFocus
      aria-label={label}
      inputMode={inputMode}
      value={text}
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => {
        // opened with Enter or a click the whole value is replaced by what is typed; opened by typing, it goes on
        if (e.target.value.length > 1) e.target.select();
      }}
      onBlur={() => end(text, "away")}
      onKeyDown={(e) => {
        if (e.key === "Enter") end(text, "grid", e.metaKey || e.ctrlKey);
        else if (e.key === "Tab") end(text, e.shiftKey ? -1 : 1);
        else if (e.key === "Escape") end(null, "grid");
        else return e.stopPropagation();
        // Esc must not also close the panel or the dialog around the grid
        e.preventDefault();
        e.stopPropagation();
      }}
      className={cn(
        "h-full w-full min-w-0 bg-background px-3 text-sm tabular-nums outline-2 -outline-offset-2 outline-(--brand) pointer-coarse:text-base",
        align === "end" && "text-right",
      )}
    />
  );
}

interface CellChoiceProps {
  label: string;
  value: string;
  options: readonly CellOption[];
  onEnd: EndEdit;
}

/**
 * The select of a cell chosen from a list, as large as the cell and already open. A choice is kept at once, Tab keeps
 * the value and opens the next cell, Esc gives up.
 */
function CellChoice({ label, value, options, onEnd }: CellChoiceProps) {
  const end = useEndOnce(onEnd);
  const select = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    try {
      select.current?.showPicker();
    } catch {
      // no user gesture behind the edit, or a browser without showPicker: the select is focused and opens as usual
    }
  }, []);
  return (
    <select
      ref={select}
      // biome-ignore lint/a11y/noAutofocus: the user opened this cell to choose its value
      autoFocus
      aria-label={label}
      value={value}
      onChange={(e) => end(e.target.value, "grid")}
      onBlur={() => end(value, "away")}
      onKeyDown={(e) => {
        if (e.key === "Tab") end(value, e.shiftKey ? -1 : 1);
        else if (e.key === "Escape") end(null, "grid");
        else return e.stopPropagation();
        // Esc must not also close the panel or the dialog around the grid
        e.preventDefault();
        e.stopPropagation();
      }}
      className="h-full w-full min-w-0 bg-background px-2 text-sm outline-2 -outline-offset-2 outline-(--brand) pointer-coarse:text-base"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/** The first column stays when the grid scrolls sideways: opaque, as tall as its row. */
const stickyCell = "sticky left-0 z-[1]";

/**
 * The --row-h token of the density (set on <html>, design-system.md §2.5) in pixels, updated when it changes: with
 * the density or when a touch screen takes the comfortable rows (ADR-050).
 */
function useDensityRowHeight(): number {
  const [height, setHeight] = useState(32);
  const coarse = useMediaQuery("(pointer: coarse)");
  // biome-ignore lint/correctness/useExhaustiveDependencies: `coarse` changes the token, so it is read again
  useEffect(() => {
    const root = document.documentElement;
    const read = () => {
      const token = getComputedStyle(root).getPropertyValue("--row-h").trim();
      const px = token.endsWith("rem")
        ? Number.parseFloat(token) * Number.parseFloat(getComputedStyle(root).fontSize)
        : Number.parseFloat(token);
      if (px > 0) setHeight(px);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(root, { attributes: true, attributeFilter: ["data-density"] });
    return () => observer.disconnect();
  }, [coarse]);
  return height;
}
